import { useQuery } from '@tanstack/react-query';
import { __ } from '../translations';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IChannelSeriesHistoryService } from '@intigral/domain/lib/application/services/ChannelSeriesHistoryService';
import user from '../helpers/user';
import h, { getCountry } from '../helpers/common';
import mpx from '../actions/mpx_data';
import { useGetBookMarksQuery } from '@intigral/domain/lib/presentation/hooks/useGetBookMarksQuery';
import { transformBookmarksResponse } from '../utils/transformResponse/jawwyResponse';
import { isGuestUser } from '../config/guestMode';

const getSeriesPlayLabel = (latestWatched, seriesHistory, episodes) => {
    const watch_now = __('watch_now');
    const resume_watching = __('resume_watching');
    const watch_next = __('watch_next');
    const nextEpisode = seriesHistory?.nextEpisode || null;
    const pad = (n: number | string) => n?.toString()?.padStart(2, '0');
    if (Object.prototype.hasOwnProperty.call(latestWatched, 'paId')) {
        const seasonNumber = latestWatched?.tvSeasonNumber || latestWatched?.seasonNumber;
        const episodeNumber = latestWatched.episodeNumber || latestWatched.tvSeasonEpisodeNumber;
        const resumeEpisode =
            episodes?.find((epi) => epi?.Id === latestWatched?.paId || epi?.Id === latestWatched?.Id) ||
            latestWatched;
        return {
            title: `${resume_watching} S${pad(seasonNumber)}, E${pad(episodeNumber)}`,
            nextEpisode: resumeEpisode,
        };
    } else if (nextEpisode) {
        const seasonNumber = nextEpisode.seasonNumber;
        const episodeNumber = nextEpisode.episodeNumber;
        const nextEpisodeData = episodes?.find((epi) => epi?.Id === nextEpisode?.id);
        return {
            title: `${watch_next} S${pad(seasonNumber)}, E${pad(episodeNumber)}`,
            nextEpisode: nextEpisodeData,
        };
    } else {
        const firstEpisode = Array.isArray(episodes) && episodes.length > 0 ? episodes[0] : null;
        const seasonNumber = firstEpisode?.tvSeasonNumber || firstEpisode?.seasonNumber;
        const episodeNumber = firstEpisode?.tvSeasonEpisodeNumber || firstEpisode?.episodeNumber;
        if (seasonNumber && episodeNumber) {
            return {
                title: `${watch_now} S${pad(seasonNumber)}, E${pad(episodeNumber)}`,
                nextEpisode: null,
            };
        }
        return {
            title: null,
            nextEpisode: null,
        };
    }
};

export const fetchSeriesHistory = (seriesId) => {
    const currentUser = user.getUserInfo();
    const isGuest = isGuestUser(currentUser);
    return useQuery({
        queryKey: ['series-history', [seriesId]],
        staleTime: 10 * 1000,
        enabled: Boolean(seriesId) && !isGuest,
        queryFn: async () => {
            try {
                const channelSeriesHistoryService = container.resolve<IChannelSeriesHistoryService>(
                    DI.SERVICES.IChannelSeriesHistoryService
                );
                const seasonHistory = await channelSeriesHistoryService.getSeriesHistory(
                    user.getAPIKey(),
                    user.getProfileName(),
                    {
                        country: getCountry(),
                        device: h.getDevice(),
                        seriesId: String(seriesId),
                    },
                    user.getUserToken(),
                    user.getIdentityToken()
                );
                return seasonHistory?.data ?? null;
            } catch (error) {
                throw error.message;
            }
        },
        refetchOnWindowFocus: false,
    });
};

const fetchSeriesEpisodes = (seriesId) => {
    return useQuery({
        queryKey: ['episodes', seriesId],
        enabled: seriesId !== null && seriesId !== undefined,
        queryFn: async () => {
            const seriesSeasons = await mpx.getSeasons(seriesId, `{PC}{${getCountry()}}`);
            if (!seriesSeasons?.length) {
                return [];
            }

            const episodes = await mpx.getEpisodes(seriesSeasons[0].Id);
            return episodes ?? [];
        },
        refetchOnWindowFocus: false,
    });
};

export const useSeriesNextEpisode = (item) => {
    const currentUser = user.getUserInfo();
    const profileGuid = user.getProfile()?.profileGuid;
    const isGuest = isGuestUser(currentUser);
    const { data: bookmarks } = useGetBookMarksQuery({
        apiKey: user.getAPIKey(),
        profileGuid: isGuest ? undefined : profileGuid,
        authToken: user.getUserToken(),
        identityToken: user.getIdentityToken(),
        country: getCountry(),
        device: h.getDevice(),
        programType: 'all',
    });
    const { data } = fetchSeriesHistory(item?.Id);
    const { data: episodes } = fetchSeriesEpisodes(item?.Id);
    let bookmarkedItem = null;
    // all the commented code will be re-use once V3 apis are deployed after release
    if (item?.programType === 'movie' || item?.programType === 'episode') {
        const movieBookmark = bookmarks?.find(
            (bookmark) => bookmark?.paId === item?.paId || bookmark?.paId == item?.Id
        );
        bookmarkedItem = { ...movieBookmark, ...(movieBookmark && item) }; // Merging item details into the bookmark for movies;
    } else {
        const episodeBookmark = bookmarks?.filter((bookmark) => episodes?.some((ep) => ep?.Id === bookmark?.paId))[0];
        bookmarkedItem = { ...episodeBookmark, ...episodes?.find((ep) => ep?.Id === episodeBookmark?.paId) }; // Merging episode details into the bookmark;
    }
    // return a stable, non-null CTA label
    // if (isGuest) {
    //     const watchNowLabel = __('watch_now');
    //     const firstEpisode = Array.isArray(episodes) && episodes.length > 0 ? episodes[0] : null;
    //     const seasonNumber = firstEpisode?.tvSeasonNumber || firstEpisode?.seasonNumber;
    //     const episodeNumber = firstEpisode?.tvSeasonEpisodeNumber || firstEpisode?.episodeNumber;
    //     const pad = (n: number | string) => n?.toString()?.padStart(2, '0');
    //     const guestTitle =
    //         seasonNumber && episodeNumber
    //             ? `${watchNowLabel} S${pad(seasonNumber)}, E${pad(episodeNumber)}`
    //             : watchNowLabel;

    //     return {
    //         title: guestTitle,
    //         nextEpisode: null,
    //         bookmarkData: { isBookmared: false, percentageNumber: null, timecode: null },
    //     };
    // }

    const { title, nextEpisode } = getSeriesPlayLabel(bookmarkedItem, data, episodes || []);
    const percentageNumber =
        (Math.ceil(Number(bookmarkedItem?.timecode)) * 100) / Math.ceil(Number(bookmarkedItem?.duration)) || null;

    const bookmarkData = {
        isBookmared: Object.prototype.hasOwnProperty.call(bookmarkedItem, 'paId') ? true : false,
        percentageNumber: percentageNumber,
        timecode: bookmarkedItem?.timecode,
    };
    return { title, nextEpisode, bookmarkData };
};
